import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement, useState } from "react";

import {
  BudgetHistoryType,
  GetBudgetsForSubaccountQuery,
  GetBudgetsForSubaccountQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import { TwoColumnDisplay } from "~frontend/components/TwoColumnDisplay";
import { GET_BUDGETS_FOR_SUBACCOUNT } from "~frontend/graphql/budgets";
import { palette } from "~frontend/theme";
import { formatDate } from "~frontend/util/dateFormat";

import { formatMillicentsAsDollars } from "../pricingV1/priceFormatting";
import { BudgetDetails } from "./BudgetDetails";
import { Budget, Subaccount } from "./types";
import { budgetIsActive } from "./util";

export function BudgetHistoryDialog({
  subaccount,
  latestBudget,
  onClose,
}: {
  subaccount: Subaccount;
  latestBudget: Budget;
  onClose: () => void;
}): ReactElement {
  const [viewingPreviousBudgetHistories, setViewingPreviousBudgetHistories] =
    useState(!budgetIsActive(latestBudget));

  const { data, loading, error } = useQuery<
    GetBudgetsForSubaccountQuery,
    GetBudgetsForSubaccountQueryVariables
  >(GET_BUDGETS_FOR_SUBACCOUNT, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  return (
    <Dialog open>
      <DialogTitle sx={{ width: "578px" }}>
        {viewingPreviousBudgetHistories ? (
          <>
            <strong>{subaccount.name}</strong> Previous Budget Histories
          </>
        ) : (
          <>
            <strong>{subaccount.name}</strong> Budget History,{" "}
            {formatDate(latestBudget.startDate)} to Present
          </>
        )}
      </DialogTitle>
      <DialogContent sx={{ width: "578px" }}>
        {viewingPreviousBudgetHistories ? (
          <AsyncWrapper data={data} loading={loading} errors={error}>
            {data => {
              const previousBudgets = data.getBudgetsForSubaccount.filter(
                budget =>
                  !budgetIsActive(budget) && budget.startDate !== budget.endDate
              );

              if (!previousBudgets.length) {
                return "No previous budget periods found.";
              }

              return (
                <FlexBox direction="column" gap="16px">
                  {previousBudgets.map(budget => (
                    <BudgetHistory
                      key={budget.id}
                      subaccount={subaccount}
                      budget={budget}
                    />
                  ))}
                </FlexBox>
              );
            }}
          </AsyncWrapper>
        ) : (
          <BudgetHistory subaccount={subaccount} budget={latestBudget} />
        )}
      </DialogContent>
      <DialogActions
        sx={{ justifyContent: "space-between", pt: 0, px: "24px", pb: "20px" }}
      >
        {viewingPreviousBudgetHistories ? (
          budgetIsActive(latestBudget) ? (
            <LinkButton
              onClick={() => setViewingPreviousBudgetHistories(false)}
            >
              <Typography variant="body3">
                View Current Budget Period
              </Typography>
            </LinkButton>
          ) : (
            <Box />
          )
        ) : (
          <LinkButton onClick={() => setViewingPreviousBudgetHistories(true)}>
            <Typography variant="body3">
              View Previous Budget Periods
            </Typography>
          </LinkButton>
        )}
        <Button variant="contained" color="tertiary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BudgetHistory({
  subaccount,
  budget,
}: {
  subaccount: Subaccount;
  budget: Budget;
}) {
  return (
    <FlexBox
      direction="column"
      gap="16px"
      sx={{ backgroundColor: palette.lightestGray, px: "24px", py: "16px" }}
    >
      <TwoColumnDisplay
        rows={budget.history.map((history, index) => {
          const previousAllocation =
            budget.history[index - 1]?.allocationInMillicents;

          let content = "";
          switch (history.type) {
            case BudgetHistoryType.BudgetCreated:
              content = `${history.createdBy.fullName} created budget at $${formatMillicentsAsDollars(history.allocationInMillicents)}`;
              break;
            case BudgetHistoryType.BudgetCreatedByReset:
              content = `${history.createdBy.fullName} reset budget at $${formatMillicentsAsDollars(history.allocationInMillicents)}`;
              break;
            case BudgetHistoryType.BudgetAllocationUpdated:
              if (history.allocationInMillicents < previousAllocation) {
                content = `${history.createdBy.fullName} decreased budget to $${formatMillicentsAsDollars(history.allocationInMillicents)}`;
              } else if (history.allocationInMillicents > previousAllocation) {
                content = `${history.createdBy.fullName} increased budget to $${formatMillicentsAsDollars(history.allocationInMillicents)}`;
              } else {
                content = `${history.createdBy.fullName} updated budget to $${formatMillicentsAsDollars(history.allocationInMillicents)}`;
              }
              break;
            case BudgetHistoryType.BudgetEnded:
              content = `${history.createdBy.fullName} ended budget period`;
          }

          return {
            key: history.id,
            label: (
              <Typography variant="body3">
                {formatDate(history.createdAt)}
              </Typography>
            ),
            content: <Typography variant="body3">{content}</Typography>,
          };
        })}
        showTopBorder={false}
      />
      <BudgetDetails
        subaccountSlug={subaccount.slug}
        startDate={budget.startDate}
        endDate={budget.endDate}
        allocationInMillicents={budget.allocationInMillicents}
        usageInMillicents={budget.usageInMillicents}
      />
    </FlexBox>
  );
}
